// src/components/Services.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  HiCode,
  HiClipboardCheck,
  HiShieldCheck,
  HiHeart
} from 'react-icons/hi';

import powerPlatformImg from '../assets/power-apps.png'; // Assuming these paths are correct
import dynamics365Img from '../assets/microsoft-d.png'; // Assuming these paths are correct

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      icon: HiCode,
      title: "Web & App Development",
      description: "Custom web applications and mobile apps built with modern technologies and best practices for optimal performance and user experience.",
      color: "from-primary-500 to-primary-700"
    },
    {
      icon: dynamics365Img,
      isImage: true,
      title: "Microsoft Dynamics 365 Solutions",
      description: "Complete Dynamics 365 implementation, customization, and integration services to streamline your business operations.",
      color: ""
    },
    {
      icon: powerPlatformImg,
      isImage: true,
      title: "Power Platform Services",
      description: "Leverage Microsoft Power Platform to build custom business applications, automate workflows, and analyze data effectively.",
      color: ""
    },
    {
      icon: HiClipboardCheck,
      title: "Process Automation",
      description: "Intelligent automation solutions to optimize business processes, reduce manual work, and increase operational efficiency.",
      color: "from-primary-400 to-secondary-600"
    },
    {
      icon: HiShieldCheck,
      title: "QA & Testing",
      description: "Comprehensive quality assurance and testing services to ensure your applications are reliable, secure, and perform flawlessly.",
      color: "from-neutral-600 to-neutral-800"
    },
    {
      icon: HiHeart,
      title: "Customer Experience Management",
      description: "End-to-end customer experience solutions to enhance customer satisfaction, loyalty, and drive business growth.",
      color: "from-secondary-400 to-accent-600"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  return (
    <section id="services" className="section-padding bg-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-32 -right-32 w-96 h-96 bg-primary-100 rounded-full opacity-25"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 0],
            x: [0, 80, 0],
            y: [0, -40, 0]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-32 -left-32 w-80 h-80 bg-secondary-100 rounded-full opacity-30"
          animate={{
            scale: [1, 1.4, 1],
            rotate: [360, 180, 0],
            x: [0, -60, 0],
            y: [0, 50, 0]
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3
          }}
        />
        <motion.div
          className="absolute top-1/3 right-1/4 w-64 h-64 bg-accent-100 rounded-full opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 360, 0],
            x: [0, 40, 0],
            y: [0, -25, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 6
          }}
        />
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 bg-primary-300 rounded-full opacity-30"
            style={{
              left: `${15 + i * 8}%`,
              top: `${20 + i * 6}%`,
            }}
            animate={{
              y: [-25, 25, -25],
              x: [-15, 15, -15],
              opacity: [0.2, 0.8, 0.2],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              duration: 10 + i * 1.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.8
            }}
          />
        ))}
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            whileInView={{
              scale: [0.9, 1.05, 1],
              opacity: [0, 1, 1]
            }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Our <span className="text-gradient">Services</span>
          </motion.h2>
          <div className="divider-gradient mb-6"></div>
          <motion.p
            variants={itemVariants}
            whileInView={{
              y: [30, 0],
              opacity: [0, 1]
            }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            We provide comprehensive technology solutions tailored to meet your business needs and drive digital transformation.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{
                scale: 1.03,
                y: -5,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)'
              }}
              className="card group cursor-pointer flex flex-col items-center text-center transition-all duration-300 bg-white/90 backdrop-blur-xl border border-neutral-200/50 hover:border-primary-200/50 card-3d hover-glow"
            >
              {service.isImage ? (
                <div className="mb-6 flex items-center justify-center w-20 h-20">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="w-14 h-14 object-contain"
                    style={{ maxWidth: '56px', maxHeight: '56px' }}
                  />
                </div>
              ) : (
                <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6`}>
                  <service.icon className="w-10 h-10 text-white" />
                </div>
              )}

              <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center"> {/* Added text-center here */}
                {service.title}
              </h3>

              <p className="text-gray-600 leading-relaxed text-center"> {/* Added text-center here */}
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
